import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * Shared file-download helper. Centralises the client-side file downloading that
 * was previously duplicated across import/report/approval components:
 *   1. `download` — fetch an API endpoint (relative path, resolved against the
 *      configured apiUrl) as a Blob through HttpClient so the auth interceptor
 *      attaches the bearer token and the binary arrives intact, then save it. A
 *      raw `<a href>` navigation to an [Authorize] endpoint sends no token and
 *      saves the 401 body as the file, corrupting the download — always go
 *      through HttpClient instead.
 *   2. `downloadFromUrl` — same, but for a fully-qualified/absolute URL.
 *   3. `saveBlob` — save an in-memory Blob to the user's machine.
 *
 * Root-provided singleton so any feature can inject and reuse it.
 */
@Injectable({ providedIn: 'root' })
export class FileDownloadService {
  /** API base, e.g. https://host/api/v1 — the same value every data service uses. */
  private readonly apiUrl = environment.apiUrl;

  constructor(private readonly http: HttpClient) {}

  /**
   * Download a file from an API endpoint given as a path RELATIVE to apiUrl
   * (e.g. "download/approval/5/excel" or "/download/approval/5/excel"). The base
   * URL is prepended here so callers never concatenate environment.apiUrl
   * themselves. Returns the request Observable (caller subscribes); the save is a
   * side effect on success.
   */
  download(relativePath: string, fileName: string): Observable<Blob> {
    const path = relativePath.startsWith('/') ? relativePath.slice(1) : relativePath;
    return this.downloadFromUrl(`${this.apiUrl}/${path}`, fileName);
  }

  /**
   * Fetch an ABSOLUTE `url` as a Blob (authenticated via HttpClient + the app's
   * interceptor) and trigger a browser "Save As" under `fileName`. Prefer
   * `download(relativePath, ...)` for normal API endpoints.
   */
  downloadFromUrl(url: string, fileName: string): Observable<Blob> {
    return this.http
      .get(url, { responseType: 'blob' })
      .pipe(tap(blob => this.saveBlob(blob, fileName)));
  }

  /**
   * Trigger a browser "Save As" for the given Blob under `fileName`.
   * Creates a temporary object URL, clicks a hidden anchor, then revokes the
   * URL to avoid leaking memory.
   */
  saveBlob(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }
}
